import { useMemo, type ReactNode } from 'react';
import { Card, CardHeader } from '../../../components/ui/Card';
import { useOpportunitiesQuery, sectionLabel } from './queries';
import { UnavailableSources } from '../../Marketing/shared';
import type { GtmContentData, GtmDemandShiftRow } from '../../../api/gtm';
import { fmtNumber, fmtPercent } from './components/format';
import { Table, THead, Th, Td, Tr, Nil, TableFooter, isConstant } from '../../../components/ui/Table';
import { EmptyState, LoadingState, ErrorState } from '../../../components/ui/States';
import { Pagination, usePagination } from '../../../components/ui/Pagination';

export function ContentView({
  startDate,
  endDate,
  afterLead,
  periodLabel,
}: {
  startDate?: string;
  endDate?: string;
  /** "Last 90 days", for the cards that compare with the period before. */
  periodLabel?: string;
  /** Rendered after the lead panel (AI citation gaps): the content calendar on Opportunities. */
  afterLead?: ReactNode;
}) {
  const query = useOpportunitiesQuery(startDate, endDate);
  const { data, isLoading, error } = query;

  if (error) {
    return (
      <div className="space-y-6">
        <ErrorState title="Couldn’t load content opportunities" error={error} onRetry={() => query.refetch()} />
        {afterLead}
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="space-y-6">
        <LoadingState rows={4} />
        {afterLead}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {data && <UnavailableSources sources={data.unavailable} label={sectionLabel} />}

      {/* Full-width, one per row: these are long-text tables, and halves truncated every prompt and URL. */}
      <AiCitationGaps data={data} />
      {afterLead}
      <h2 className="text-body-md font-medium text-text-primary">Gaps and quick wins</h2>
      <CitedSources data={data} />

      <ClusterCoverage data={data} />

      <QueryOpportunities data={data} />

      <IcpTraffic data={data} />
      <ContentConversion data={data} />

      <DemandShifts data={data} periodLabel={periodLabel ?? 'Selected period'} />
      <Glossary data={data} />
    </div>
  );
}

function AiCitationGaps({ data }: { data?: GtmContentData }) {
  const rows = data?.aiCitationGaps;
  const pager = usePagination(rows ?? NO_ROWS, PAGE_SIZE);
  return (
    <Card>
      <CardHeader title="AI citation gaps" description="Prompts where we're absent from AI answers: content to create/optimise (Peec)" />
      {!rows || rows.length === 0 ? (
        <Empty label="No citation gaps, or Peec has no own brand configured." />
      ) : (
        <>
        <Table colWidths={['auto', '16rem', '8rem']}>
          <THead>
            <tr>
              <Th>Prompt</Th>
              <Th>Topic</Th>
              <Th align="right">Demand</Th>
            </tr>
          </THead>
          <tbody>
            {pager.pageItems.map((r) => (
              <Tr key={r.promptId}>
                <Td className="text-text-primary break-words" title={r.promptText}>{r.promptText}</Td>
                <Td className="text-text-secondary break-words" title={r.topic ?? ''}>{r.topic ?? <Nil />}</Td>
                <Td align="right" className="capitalize whitespace-nowrap">{r.volume ?? <Nil />}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
        <Pagination page={pager.page} pageCount={pager.pageCount} total={pager.total} pageSize={pager.pageSize} onPage={pager.setPage} />
        </>
      )}
    </Card>
  );
}

function CitedSources({ data }: { data?: GtmContentData }) {
  const rows = useMemo(
    // A page naming no tracked brand is a competitor's own site, not a page to get listed on (the API drops them too).
    () =>
      data?.citedSources
        ? data.citedSources.filter((r) => r.mentionedBrandCount > 0).sort((a, b) => b.citationCount - a.citationCount)
        : undefined,
    [data?.citedSources]
  );
  const pager = usePagination(rows ?? NO_ROWS, PAGE_SIZE);
  return (
    <Card>
      <CardHeader title="Pages AI cites for competitors, not us" description="URLs AI answers cite where competitors are mentioned and we aren’t: pages to outrank or get listed on (Peec)" />
      {!rows || rows.length === 0 ? (
        <Empty label="No competitor-only cited sources for this period." />
      ) : (
        <>
        <Table colWidths={['auto', '6rem', '6rem']}>
          <THead>
            <tr>
              <Th>Cited page</Th>
              <Th align="right" title="How often AI answers cited this page in the period">Times cited</Th>
              <Th align="right" title="How many tracked brands the page mentions">Brands named</Th>
            </tr>
          </THead>
          <tbody>
            {pager.pageItems.map((r, i) => (
              <Tr key={`${r.url ?? r.domain}-${i}`}>
                <Td className="text-text-primary break-words" title={r.url ?? r.domain}>
                  {r.url ? (
                    <a href={r.url} target="_blank" rel="noreferrer" className="hover:underline">{(r.title && unescapeMarkdown(r.title)) || stripDomain(r.url)}</a>
                  ) : r.domain}
                  <span className="text-text-tertiary"> · {r.domain}</span>
                </Td>
                <Td align="right">{fmtNumber(r.citationCount)}</Td>
                <Td align="right">{fmtNumber(r.mentionedBrandCount)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
        <Pagination page={pager.page} pageCount={pager.pageCount} total={pager.total} pageSize={pager.pageSize} onPage={pager.setPage} />
        </>
      )}
    </Card>
  );
}

/** Past page two: the point where an average position is worth flagging. */
const WEAK_POSITION = 20;

/** Locale prefixes on the site (/fr/, /de/, /nl/…): a language, not a section. */
const LOCALE_SEGMENTS = new Set(['en', 'fr', 'de', 'nl', 'es', 'it', 'pt', 'be', 'at', 'ch']);

/** Turn a raw cluster slug (URL path segment) into a readable label. */
function clusterLabel(cluster: string): string {
  if (cluster === 'Unclustered' || cluster === 'Homepage') return cluster;
  // The Semrush sync clusters on the first path segment, so every localised URL
  // lands under its locale prefix. Say so rather than calling "fr" a section.
  // The API now sections localised URLs (siteSection); older payloads still say it once, under the table.
  if (LOCALE_SEGMENTS.has(cluster.toLowerCase())) return `/${cluster.toLowerCase()}/ pages`;
  // "solutions" → "Solutions", "case-studies" → "Case studies"
  const spaced = cluster.replace(/[-_]/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function ClusterCoverage({ data }: { data?: GtmContentData }) {
  const rows = data?.clusterCoverage;
  const pager = usePagination(rows ?? NO_ROWS, 15);
  // Amber flags an exception. When most sections sit past the threshold that is the
  // norm, not a warning, so the column stays plain and the footer says it once.
  const pastThreshold = rows?.filter((r) => r.avgPosition !== null && r.avgPosition > WEAK_POSITION).length ?? 0;
  const flagWeak = !!rows && rows.length > 0 && pastThreshold / rows.length < 0.5;
  // The cluster dimension only adds signal once it's more than one bucket; if
  // every row is still "Unclustered", flag that a fresh Semrush sync is needed.
  const allUnclustered = !!rows && rows.length > 0 && rows.every((r) => r.cluster === 'Unclustered');
  const hasLocaleRows = !!rows && rows.some((r) => LOCALE_SEGMENTS.has(r.cluster.toLowerCase()));
  return (
    <Card>
      <CardHeader
        title="Cluster coverage"
        description="How well each site section ranks per market: your biggest organic opportunities first. Lower avg position is better; page 1 = top 10 (Semrush)."
      />
      {!rows || rows.length === 0 ? (
        <Empty label="No Semrush rankings yet: connect Semrush and run a sync." />
      ) : (
        <>
          {allUnclustered && (
            <p className="mb-3 text-body-xs text-text-tertiary">
              Clusters not yet assigned: run a fresh Semrush rankings sync to group keywords by site section.
            </p>
          )}
          <Table colWidths={['auto', '5rem', '6rem', '6rem', '8rem', '6rem']} minWidth={560}>
            <THead>
              <tr>
                <Th>Section</Th>
                <Th>Market</Th>
                <Th align="right">Keywords</Th>
                <Th align="right" title="Total monthly search volume across the section's keywords">Volume</Th>
                <Th align="right" title="Keywords ranking in the top 10, and the share of the section's keywords that reach page one">Page 1</Th>
                <Th align="right" title="Average ranking position: lower is better; amber past page two when that is the exception">Avg pos</Th>
              </tr>
            </THead>
            <tbody>
              {pager.pageItems.map((r) => {
                const page1Share = r.keywordCount > 0 ? (r.page1Count / r.keywordCount) * 100 : 0;
                // Neutral by default; colour only flags the weak case (beyond page two).
                const weak = flagWeak && r.avgPosition !== null && r.avgPosition > WEAK_POSITION;
                return (
                  <Tr key={`${r.cluster}-${r.market}`}>
                    <Td className="break-words text-text-primary" title={clusterLabel(r.cluster)}>{clusterLabel(r.cluster)}</Td>
                    <Td className="uppercase">{r.market}</Td>
                    <Td align="right">{fmtNumber(r.keywordCount)}</Td>
                    <Td align="right">{r.searchVolume === null ? <Nil /> : fmtNumber(r.searchVolume)}</Td>
                    <Td align="right">
                      {fmtNumber(r.page1Count)}
                      <span className="text-text-tertiary"> · {fmtPercent(page1Share, 0)}</span>
                    </Td>
                    <Td align="right" className={`tabular-nums ${weak ? 'text-warning-dark' : 'text-text-secondary'}`}>
                      {r.avgPosition === null ? <Nil /> : r.avgPosition.toFixed(1)}
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
          <Pagination page={pager.page} pageCount={pager.pageCount} total={pager.total} pageSize={pager.pageSize} onPage={pager.setPage} />
          {((!flagWeak && pastThreshold > 0) || hasLocaleRows) && (
            <TableFooter>
              {[
                !flagWeak && pastThreshold > 0 &&
                  `${fmtNumber(pastThreshold)} of ${fmtNumber(rows.length)} rows average past position ${WEAK_POSITION} (beyond page two).`,
                hasLocaleRows && 'Localised pages (/fr/, /de/, /nl/) are one row per language, not split by section.',
              ]
                .filter(Boolean)
                .join(' ')}
            </TableFooter>
          )}
        </>
      )}
    </Card>
  );
}

/**
 * Junk queries: Google searches made of two or more quoted phrases, e.g.
 * `"shawn brown" "tel staffing and hr"`. They are people looking up a named
 * person at a company (recruiter-style operator searches), not content demand.
 * The rule is deliberately that simple; a hidden-row count says what it caught.
 */
const QUOTED_LOOKUP = /"[^"]+"[^"]*"[^"]+"/;

function isJunkQuery(query: string): boolean {
  return QUOTED_LOOKUP.test(query);
}

type OpportunityRow = {
  query: string;
  kind: 'quick_win' | 'gap' | 'both';
  position: number;
  impressions: number;
  ctr: number | null;
  searchVolume: number | null;
};

const KIND_LABEL: Record<OpportunityRow['kind'], string> = {
  quick_win: 'Quick win',
  gap: 'Content gap',
  both: 'Both',
};

/**
 * Quick wins (page-two terms) and the content gap tracker (demand with a weak
 * page) list many of the same queries, so they share one table: one row per
 * query, tagged with where it came from.
 */
function QueryOpportunities({ data }: { data?: GtmContentData }) {
  const { rows, hidden } = useMemo(() => {
    const byQuery = new Map<string, OpportunityRow>();
    for (const r of data?.quickWins ?? []) {
      byQuery.set(r.query, { query: r.query, kind: 'quick_win', position: r.position, impressions: r.impressions, ctr: r.ctr, searchVolume: null });
    }
    for (const r of data?.contentGaps ?? []) {
      const cur = byQuery.get(r.query);
      byQuery.set(
        r.query,
        cur
          ? { ...cur, kind: 'both', searchVolume: r.searchVolume }
          : { query: r.query, kind: 'gap', position: r.position, impressions: r.impressions, ctr: null, searchVolume: r.searchVolume },
      );
    }
    const all = [...byQuery.values()].sort((x, y) => y.impressions - x.impressions);
    const kept = all.filter((r) => !isJunkQuery(r.query));
    return { rows: kept, hidden: all.length - kept.length };
  }, [data?.quickWins, data?.contentGaps]);
  const pager = usePagination(rows, PAGE_SIZE);
  // DataForSEO volume is often missing for every query: then say so once instead of a column of dots.
  const hasVolume = rows.some((r) => r.searchVolume !== null && r.searchVolume > 0);
  const hasCtr = rows.some((r) => r.ctr !== null);
  const oneKind = isConstant(rows, (r) => r.kind);
  const widths = ['auto', ...(oneKind ? [] : ['7.5rem']), '5rem', '7rem', ...(hasCtr ? ['5rem'] : []), ...(hasVolume ? ['6rem'] : [])];
  return (
    <Card>
      <CardHeader
        title="Quick wins and content gaps"
        description={
          <>
            Page-two terms one push from page one, and demand with a weak or missing page (GSC).{' '}
            {!hasVolume && rows.length > 0 && 'No search volume column: DataForSEO returned no volumes for these queries.'}
            {oneKind && rows[0] && ` All ${KIND_LABEL[rows[0].kind].toLowerCase()}s.`}
          </>
        }
      />
      {rows.length === 0 ? (
        <Empty label={hidden > 0 ? 'Only junk queries in this period.' : 'No quick wins or content gaps for this period.'} />
      ) : (
        <>
          <Table colWidths={widths}>
            <THead>
              <tr>
                <Th>Query</Th>
                {!oneKind && <Th>Type</Th>}
                <Th align="right">Pos</Th>
                <Th align="right">Impr.</Th>
                {hasCtr && <Th align="right">CTR</Th>}
                {hasVolume && <Th align="right">Volume</Th>}
              </tr>
            </THead>
            <tbody>
              {pager.pageItems.map((r) => (
                <Tr key={r.query}>
                  <Td className="text-text-primary break-words" title={r.query}>{r.query}</Td>
                  {!oneKind && <Td className="text-text-secondary whitespace-nowrap">{KIND_LABEL[r.kind]}</Td>}
                  <Td align="right">{r.position.toFixed(1)}</Td>
                  <Td align="right">{fmtNumber(r.impressions)}</Td>
                  {hasCtr && <Td align="right">{r.ctr === null ? <Nil /> : fmtPercent(r.ctr * 100)}</Td>}
                  {hasVolume && <Td align="right">{r.searchVolume === null ? <Nil /> : fmtNumber(r.searchVolume)}</Td>}
                </Tr>
              ))}
            </tbody>
          </Table>
          <Pagination page={pager.page} pageCount={pager.pageCount} total={pager.total} pageSize={pager.pageSize} onPage={pager.setPage} />
        </>
      )}
      {hidden > 0 && (
        <TableFooter>
          {hidden} junk {hidden === 1 ? 'query' : 'queries'} hidden (two or more quoted phrases, e.g. a name and a company)
        </TableFooter>
      )}
    </Card>
  );
}

function DemandShifts({ data, periodLabel }: { data?: GtmContentData; periodLabel: string }) {
  const m = data?.demandShifts;
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader title="Emerging demand" description={`${periodLabel} · queries with 50+ impressions that had under 10 in the period before (GSC)`} />
        <QueryTable rows={m?.emerging} />
      </Card>
      <Card>
        <CardHeader title="Declining demand" description={`${periodLabel} · queries with less than half the impressions of the period before (GSC)`} />
        <QueryTable rows={m?.declining} />
      </Card>
    </div>
  );
}

const NO_ROWS: never[] = [];
const PAGE_SIZE = 15;

function Glossary({ data }: { data?: GtmContentData }) {
  const rows = data?.glossary;
  const pager = usePagination(rows ?? NO_ROWS, 15);
  return (
    <Card>
      <CardHeader title="Glossary segment" description="Organic performance of glossary pages (GSC)" />
      {!rows || rows.length === 0 ? (
        <Empty label="No glossary pages with impressions in this period." />
      ) : (
        <>
        <Table colWidths={['auto', '6rem', '6rem', '5rem']}>
          <THead>
            <tr>
              <Th>Page</Th>
              <Th align="right">Clicks</Th>
              <Th align="right">Impr.</Th>
              <Th align="right">Pos</Th>
            </tr>
          </THead>
          <tbody>
            {pager.pageItems.map((r) => (
              <Tr key={r.page}>
                <Td className="text-text-primary break-words" title={r.page}>{stripDomain(r.page)}</Td>
                <Td align="right">{fmtNumber(r.clicks)}</Td>
                <Td align="right">{fmtNumber(r.impressions)}</Td>
                <Td align="right">{r.position.toFixed(1)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
        <Pagination page={pager.page} pageCount={pager.pageCount} total={pager.total} pageSize={pager.pageSize} onPage={pager.setPage} />
        </>
      )}
    </Card>
  );
}

/** The ICP rule, in words, for the card's footnote and its empty line. */
const ICP_RULE =
  'ICP = the company’s Industry / ICP category in Attio; for a company not in Attio (or with no category there), 50+ staff by RB2B’s band, schools and universities left out.';

/**
 * Why identified visitors (ISS, say) still add up to no ICP-fit traffic. RB2B
 * often sends no headcount, which can never meet the size bar.
 */
function icpZeroReason(basis: GtmContentData['icpTrafficBasis']): string {
  if (!basis) return 'No ICP-fit traffic in this period (RB2B), so the ICP-fit traffic card is hidden.';
  const names = basis.nonIcpNames.length > 0 ? ` (${basis.nonIcpNames.join(', ')}${basis.companies > basis.nonIcpNames.length ? '…' : ''})` : '';
  return (
    `No ICP-fit traffic in this period, so the card is hidden. ${basis.companies} identified ${basis.companies === 1 ? 'company' : 'companies'} visited${names}; none is ICP. ` +
    ICP_RULE +
    (basis.noSize > 0 ? ` ${basis.noSize} not in Attio had no headcount from RB2B, so they can’t qualify.` : '')
  );
}

/** Fewer total visits than this and "100% ICP" is one company's single visit, not a pattern: the share shows n/a. */
const ICP_SHARE_MIN_VISITS = 3;

function IcpTraffic({ data }: { data?: GtmContentData }) {
  const all = data?.icpTraffic;
  // Pages with no ICP visit are most of the list and say nothing: only pages ICP companies read are rows.
  const rows = (all ?? []).filter((r) => r.icpVisits > 0);
  const hidden = (all?.length ?? 0) - rows.length;
  // Nothing to show: the card goes, and one muted line says why.
  if (!all || all.length === 0 || rows.length === 0) {
    return (
      <p className="px-1 text-body-xs text-text-tertiary">
        {all && all.length > 0 ? icpZeroReason(data?.icpTrafficBasis ?? null) : 'No page-level visitor data from RB2B yet, so the ICP-fit traffic card is hidden.'}
      </p>
    );
  }
  return (
    <Card>
      <CardHeader title="ICP-fit traffic by content" description="Pages read by the companies we sell to (RB2B visits, Attio ICP)" />
      <Table colWidths={['auto', '6rem', '5rem', '6rem', '16rem']}>
          <THead>
            <tr>
              <Th>Page</Th>
              <Th align="right">ICP visits</Th>
              <Th align="right">Total</Th>
              <Th align="right">ICP share</Th>
              <Th className="pl-4">Top ICP companies</Th>
            </tr>
          </THead>
          <tbody>
            {rows.map((r) => (
              <Tr key={r.pagePath}>
                <Td className="text-text-primary break-all">{r.pagePath}</Td>
                <Td align="right">{fmtNumber(r.icpVisits)}</Td>
                <Td align="right">{fmtNumber(r.totalVisits)}</Td>
                <Td align="right" title={r.totalVisits < ICP_SHARE_MIN_VISITS ? `Only ${r.totalVisits} ${r.totalVisits === 1 ? 'visit' : 'visits'}, too few for a share` : undefined}>
                  {r.totalVisits >= ICP_SHARE_MIN_VISITS ? fmtPercent(r.icpShare * 100) : <Nil />}
                </Td>
                <Td className="pl-4 text-text-secondary break-words">
                  {r.topIcpCompanies.length > 0 ? r.topIcpCompanies.join(', ') : <Nil />}
                </Td>
              </Tr>
            ))}
          </tbody>
      </Table>
      <TableFooter>
        {ICP_RULE} The share needs at least {ICP_SHARE_MIN_VISITS} visits to the page, below that it shows a dash.
        {hidden > 0 && ` ${hidden} ${hidden === 1 ? 'page' : 'pages'} with no ICP visit hidden.`}
      </TableFooter>
    </Card>
  );
}

function ContentConversion({ data }: { data?: GtmContentData }) {
  const rows = data?.contentConversion;
  // Nothing linked yet: the card would only say so, so it is left out.
  if (!rows || rows.length === 0) return null;
  return (
    <Card>
      <CardHeader title="Content → conversion" description="Pages converting companies viewed before submitting a form/demo request (behavioural spine)" />
      <Table colWidths={['auto', '10rem', '9rem']}>
          <THead>
            <tr>
              <Th>Page</Th>
              <Th align="right">Converting visitors</Th>
              <Th align="right">Total visitors</Th>
            </tr>
          </THead>
          <tbody>
            {rows.map((r) => (
              <Tr key={r.pagePath}>
                <Td className="text-text-primary break-words" title={r.pagePath}>{stripDomain(r.pagePath)}</Td>
                <Td align="right">{fmtNumber(r.convertingVisitors)}</Td>
                <Td align="right">{r.totalVisitors === null ? <Nil /> : fmtNumber(r.totalVisitors)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
    </Card>
  );
}


function QueryTable({ rows: allRows }: { rows?: GtmDemandShiftRow[] | null }) {
  const rows = useMemo(() => allRows?.filter((r) => !isJunkQuery(r.query)) ?? null, [allRows]);
  const hidden = (allRows?.length ?? 0) - (rows?.length ?? 0);
  const pager = usePagination(rows ?? NO_ROWS, PAGE_SIZE);
  if (!rows || rows.length === 0) {
    return <Empty label="No data for this period." />;
  }
  // The prior period's impressions make the shift itself visible (API from 13 Oct; older payloads lack it).
  const hasPrior = rows.some((r) => r.previousImpressions !== undefined);
  // CTR on these long-tail queries is 0.0% on nearly every row: said once instead of a column of zeros.
  const showCtr = rows.filter((r) => Number((r.ctr * 100).toFixed(1)) > 0).length >= Math.ceil(rows.length / 3);
  return (
    <>
    <Table colWidths={['auto', '5rem', ...(hasPrior ? ['6.5rem'] : []), '6.5rem', ...(hasPrior ? ['6rem'] : []), ...(showCtr ? ['5rem'] : [])]}>
      <THead>
        <tr>
          <Th>Query</Th>
          <Th align="right">Pos</Th>
          {hasPrior && <Th align="right" title="Impressions in the same-length period before">Prior impr.</Th>}
          <Th align="right">Impr.</Th>
          {hasPrior && <Th align="right">Change</Th>}
          {showCtr && <Th align="right">CTR</Th>}
        </tr>
      </THead>
      <tbody>
        {pager.pageItems.map((r) => (
          <Tr key={r.query}>
            <Td className="text-text-primary break-words" title={r.query}>{r.query}</Td>
            <Td align="right">{r.position.toFixed(1)}</Td>
            {hasPrior && <Td align="right">{r.previousImpressions ? fmtNumber(r.previousImpressions) : <Nil />}</Td>}
            <Td align="right">{fmtNumber(r.impressions)}</Td>
            {hasPrior && (
              <Td align="right" className="tabular-nums">
                {r.previousImpressions === undefined ? <Nil /> : `${r.impressions >= r.previousImpressions ? '+' : '−'}${fmtNumber(Math.abs(r.impressions - r.previousImpressions))}`}
              </Td>
            )}
            {showCtr && <Td align="right">{fmtPercent(r.ctr * 100)}</Td>}
          </Tr>
        ))}
      </tbody>
    </Table>
    <Pagination page={pager.page} pageCount={pager.pageCount} total={pager.total} pageSize={pager.pageSize} onPage={pager.setPage} />
    {(hidden > 0 || !showCtr) && (
      <TableFooter>
        {[
          !showCtr && 'CTR hidden: 0% or close on almost every query',
          hidden > 0 && `${hidden} junk ${hidden === 1 ? 'query' : 'queries'} hidden (two or more quoted phrases)`,
        ]
          .filter(Boolean)
          .join(' · ')}
      </TableFooter>
    )}
    </>
  );
}

function Empty({ label }: { label: string }) {
  return <EmptyState compact title={label} />;
}

/** Peec titles arrive markdown-escaped ("Gartner \\| Magic Quadrant"); show them as written. */
function unescapeMarkdown(text: string): string {
  return text.replace(/\\([\\`*_{}[\]()#+\-.!|])/g, '$1');
}

function stripDomain(url: string): string {
  try {
    const u = new URL(url);
    return u.pathname + u.search;
  } catch {
    return url;
  }
}
